import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Mail, Instagram, Twitter, Facebook } from 'lucide-react';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(`Subscribed! Use code ${response.data.discount_code} for 10% off!`);
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#050505] border-t border-[#27272A]" data-testid="footer">
      <div className="container-main py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand & Newsletter */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_goofy-hawking-1/artifacts/8ew7ku5q_photo-output.png" 
                alt="Pure Shmacks" 
                className="h-20 w-auto object-contain"
              />
            </Link>
            <p className="text-white/60 mb-6 max-w-md">
              Premium authenticated sports memorabilia. Every item verified. Every autograph guaranteed.
            </p>
            
            {/* Newsletter */}
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Enter your email for 10% off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field flex-1"
                data-testid="newsletter-email-input"
              />
              <Button type="submit" className="btn-primary h-12 px-6" disabled={loading} data-testid="newsletter-submit-btn">
                <Mail className="w-5 h-5" />
              </Button>
            </form>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-barlow font-bold uppercase tracking-wider text-white mb-6">Shop</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/products" className="text-white/60 hover:text-white transition-colors duration-200" data-testid="footer-link-all-products">
                  All Products
                </Link>
              </li>
              <li>
                <Link to="/products?category=mystery_box" className="text-white/60 hover:text-white transition-colors duration-200" data-testid="footer-link-mystery-boxes">
                  Mystery Boxes
                </Link>
              </li>
              <li>
                <Link to="/products?category=jersey" className="text-white/60 hover:text-white transition-colors duration-200" data-testid="footer-link-jerseys">
                  Jerseys
                </Link>
              </li>
              <li>
                <Link to="/products?category=helmet" className="text-white/60 hover:text-white transition-colors duration-200" data-testid="footer-link-helmets">
                  Helmets
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-barlow font-bold uppercase tracking-wider text-white mb-6">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/wholesale" className="text-white/60 hover:text-white transition-colors duration-200" data-testid="footer-link-wholesale">
                  Wholesale
                </Link>
              </li>
              <li>
                <Link to="/sell-to-us" className="text-white/60 hover:text-white transition-colors duration-200" data-testid="footer-link-sell-to-us">
                  Sell To Us
                </Link>
              </li>
              <li>
                <span className="text-white/60">Authenticity Guarantee</span>
              </li>
              <li>
                <span className="text-white/60">Contact Us</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#27272A] flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Pure Shmacks. All rights reserved.
          </p>
          
          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a href="#" className="text-white/40 hover:text-white transition-colors duration-200" aria-label="Instagram">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="#" className="text-white/40 hover:text-white transition-colors duration-200" aria-label="Twitter">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="#" className="text-white/40 hover:text-white transition-colors duration-200" aria-label="Facebook">
              <Facebook className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
